import { useState } from "react";
import { Link } from "react-router-dom";

type Item = { label: string; to?: string; href?: string };

type Fn = {
  key: string;
  title: string;
  summary: string;
  items: Item[];
};

const functions: Fn[] = [
  {
    key: "connect",
    title: "Connect",
    summary: "Humans finding each other across countries, cities and disciplines.",
    items: [
      { label: "Human Mycelium", to: "/human-mycelium" },
      { label: "Project Octopus", to: "/project-octopus" },
      { label: "Mycelial connection", to: "/mycelial-connection" },
    ],
  },
  {
    key: "think",
    title: "Think",
    summary: "Nonlinear thinking put to work on real complexity, together.",
    items: [
      { label: "Think Tank Institute", href: "https://octopus-thinktank.institute/" },
      { label: "Nonlinear thinking", to: "/nonlinear-thinking" },
      { label: "Think Tanks", to: "/think-tank" },
    ],
  },
  {
    key: "listen",
    title: "Listen",
    summary: "Questions, complaints and signals from humans, taken seriously.",
    items: [
      { label: "TellTOM", to: "/telltom" },
      { label: "Wall of Thoughts", to: "/wall-of-thoughts" },
      { label: "The six crises", to: "/crisis/well-being" },
    ],
  },
  {
    key: "hold",
    title: "Hold space",
    summary: "Rooms where humans can simply arrive, without performance.",
    items: [
      { label: "Octopus Anonymous", to: "/octopus-anonymous" },
      { label: "Conversations", href: "https://chat.whatsapp.com/FkTpfnaJRFdDl2CfJFu7qs?mode=gi_t" },
      { label: "Member spaces", to: "/membership" },
    ],
  },
  {
    key: "create",
    title: "Create",
    summary: "Projects, experiments and the things that have not been invented yet.",
    items: [
      { label: "Kids Think Tank", to: "/kids-council" },
      { label: "Human Score", href: "https://humanscore.info/" },
      { label: "iBUILD", href: "https://ibuild.theoctopusmovement.org/" },
    ],
  },
  {
    key: "learn",
    title: "Learn & share",
    summary: "Science, wisdom, writing and everything we pass on.",
    items: [
      { label: "Science Members", to: "/science-member" },
      { label: "Wisdom Membership", to: "/wisdom-membership" },
      { label: "The Engine Room", href: "https://members.theoctopusmovement.org/home" },
    ],
  },
];

// Positions on the radial diagram, in percent of the square container.
const positions = [
  { x: 50, y: 6 },
  { x: 89, y: 28 },
  { x: 89, y: 72 },
  { x: 50, y: 94 },
  { x: 11, y: 72 },
  { x: 11, y: 28 },
];

const OrganismSection = () => {
  const [active, setActive] = useState(functions[0].key);
  const activeFn = functions.find((f) => f.key === active) ?? functions[0];

  return (
    <section id="organism" className="py-24 md:py-32 bg-background overflow-hidden">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-14">
          <p className="text-sm uppercase tracking-[0.2em] text-coral-text mb-4">The organism</p>
          <h2 className="text-3xl md:text-5xl font-heading font-bold mb-6">
            One living body of{" "}
            <span className="text-gradient-coral">human intelligence.</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Not a menu of departments. Six living functions growing out of one Human Mycelium. Touch a function to see
            what is alive underneath it.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Radial diagram */}
          <div className="relative w-full max-w-xl mx-auto aspect-square">
            <svg
              className="absolute inset-0 w-full h-full text-primary/30"
              viewBox="0 0 100 100"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="50" cy="50" r="34" stroke="currentColor" strokeWidth="0.3" strokeDasharray="1.5 2" />
              <circle cx="50" cy="50" r="44" stroke="currentColor" strokeWidth="0.2" strokeDasharray="1 3" />
              {positions.map((p, i) => {
                const mx = (50 + p.x) / 2 + (i % 2 === 0 ? 5 : -5);
                const my = (50 + p.y) / 2 + (i % 3 === 0 ? -5 : 5);
                const isActive = functions[i].key === active;
                return (
                  <path
                    key={functions[i].key}
                    d={`M50 50 Q ${mx} ${my} ${p.x} ${p.y}`}
                    stroke="currentColor"
                    strokeWidth={isActive ? 0.9 : 0.4}
                    strokeDasharray="2 2"
                    className={`transition-all duration-500 ${isActive ? "text-primary" : ""}`}
                  />
                );
              })}
            </svg>

            {/* Centre */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
              <div className="relative w-32 h-32 md:w-40 md:h-40 flex items-center justify-center">
                <span className="absolute inset-0 rounded-full bg-primary/10 animate-pulse-glow" aria-hidden="true" />
                <span className="absolute inset-3 rounded-full border border-primary/50" aria-hidden="true" />
                <span className="relative px-4 text-sm md:text-base font-heading font-bold uppercase tracking-[0.15em] text-foreground leading-tight">
                  Human
                  <br />
                  Mycelium
                </span>
              </div>
            </div>

            {/* Function nodes */}
            {functions.map((fn, i) => {
              const p = positions[i];
              const isActive = fn.key === active;
              return (
                <button
                  key={fn.key}
                  type="button"
                  onClick={() => setActive(fn.key)}
                  onMouseEnter={() => setActive(fn.key)}
                  aria-pressed={isActive}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 min-h-[44px] min-w-[44px] px-4 py-2 rounded-full border text-xs md:text-sm font-heading font-bold uppercase tracking-[0.1em] transition-all duration-300 transform-gpu ${
                    isActive
                      ? "border-primary bg-primary text-primary-foreground scale-105"
                      : "border-border bg-background text-muted-foreground hover:border-primary/60 hover:text-foreground"
                  }`}
                  style={{ left: `${p.x}%`, top: `${p.y}%` }}
                >
                  {fn.title}
                </button>
              );
            })}
          </div>

          {/* Detail panel */}
          <div className="lg:pl-8">
            <p className="text-sm uppercase tracking-[0.2em] text-coral-text mb-3">{activeFn.title}</p>
            <h3 className="text-2xl md:text-3xl font-heading font-bold mb-4 leading-snug">{activeFn.summary}</h3>
            <ul className="space-y-3">
              {activeFn.items.map((item) => (
                <li key={item.label}>
                  {item.to ? (
                    <Link
                      to={item.to}
                      className="inline-flex items-center gap-2 min-h-[44px] text-foreground hover:text-primary transition-colors"
                    >
                      <span className="w-6 h-[1px] bg-primary/50" aria-hidden="true" /> {item.label}
                    </Link>
                  ) : (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 min-h-[44px] text-foreground hover:text-primary transition-colors"
                    >
                      <span className="w-6 h-[1px] bg-primary/50" aria-hidden="true" /> {item.label} ↗
                    </a>
                  )}
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-2 mt-10">
              {functions.map((fn) => (
                <button
                  key={fn.key}
                  type="button"
                  onClick={() => setActive(fn.key)}
                  className={`px-4 py-2 rounded-full text-xs font-medium border transition-all ${
                    fn.key === active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  {fn.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrganismSection;